/* eslint-disable camelcase */
import { useEffect, useCallback } from 'react';

import { useTranslation } from 'react-i18next';

import { showError } from '@store/logged/actions';
import { hideLoading, showLoading } from '@store/common/actions';
import { useDispatch, useSelector } from 'react-redux';
import {
  getUsers,
  setUserSelected,
  update as doUpdate,
  updatePhone as doUpdatePhone,
  blockUser,
  deleteUser as dispatchDeleteUser,
  changeFilter as doChangeFilter,
  resetFilter as doResetFilter,
  inviteUser as doInviteUser,
} from '@store/users/actions';
import { resendEmail } from '@services/users-service';

import { successResponsive } from '@constants/errorObjects';
import {
  ADMIN,
  ADMIN_SECONDARY,
  SELLER,
  SUPER_ADMIN_ONUS,
  ADMIN_ONUS,
  ANALISTA_ONUS,
  USER_STATUS,
} from '@constants/user';
import { isAllowed } from '@helpers/auth';
import { useAuth } from '@hooks';

const useUser = ({ fetchUsers = true } = {}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { selected, list: users, filter } = useSelector((state) => state.users);
  const {
    loguedAs: { doc_number: loggedDoc, roles: currentRoles },
  } = useSelector((state) => state.login);
  const { isSeller } = useAuth();

  useEffect(() => {
    if (fetchUsers) dispatch(getUsers());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resendInvitation = useCallback(
    (email) => {
      dispatch(showLoading());
      resendEmail(email)
        .then(() =>
          dispatch(showError(successResponsive(t('users.actions.resendInvitationSuccess'))))
        )
        .catch((error) => dispatch(showError(error)))
        .finally(() => dispatch(hideLoading()));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  const selectUser = useCallback(
    (user) => {
      dispatch(setUserSelected(user));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  const inviteUser = useCallback((data) => dispatch(doInviteUser(data)), [dispatch]);
  const update = useCallback((data) => dispatch(doUpdate(data)), [dispatch]);
  const updatePhone = useCallback((phone) => dispatch(doUpdatePhone(phone)), [dispatch]);
  const unsubscribeUser = useCallback(
    ({ action, ...user }) => {
      dispatch(blockUser(user, action, users));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  const deleteUser = useCallback(
    ({ action, ...user }) => {
      dispatch(dispatchDeleteUser(user, action, users));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  /**
   * Compara roles de usuarios: el @currentRole sea igual o superior a @role
   * @param {string} role Rol que determina equivalencia
   * @param {string} currentRole Rol a consultar equivalencia
   */
  const isEquivalentRole = (role, currentRole) => {
    switch (currentRole) {
      case SUPER_ADMIN_ONUS:
      case ADMIN:
        return true;

      case ADMIN_ONUS:
        return role === ADMIN_ONUS || role === ANALISTA_ONUS;
      case ADMIN_SECONDARY:
        return role === ADMIN_SECONDARY || role === SELLER;

      case ANALISTA_ONUS:
        return role === ANALISTA_ONUS;
      case SELLER:
        return role === SELLER;

      default:
        return false;
    }
  };

  const changeFilter = useCallback((newFilter) => dispatch(doChangeFilter(newFilter)), [dispatch]);

  const resetFilter = useCallback(() => dispatch(doResetFilter()), [dispatch]);

  const getPermissionActionsByUser = (user) => {
    const { VERIFY_EMAIL, UNSUBSCRIBE, APPROVED } = USER_STATUS;
    const statusPending = user.status === VERIFY_EMAIL;
    const statusApproved = user.status === APPROVED;
    const statusBlocked = user.status === UNSUBSCRIBE;

    const canMakeEdit = isAllowed('USERS_UPDATE_PROFILE') && !user.is_legal_agent;
    const canMakeEditRL = isAllowed('MERCHANT_UPDATE') && user.is_legal_agent && !isSeller();
    const canMakeUnsubscribe = isAllowed('USERS_UNSUBSCRIBE');
    const canMakeDelete = isAllowed('USERS_DELETED');
    const canBeBlockedByCurrentRole = (roleUser) => isEquivalentRole(roleUser, currentRoles[0]);

    const isMyUser = user.doc_number === loggedDoc;

    const canResendInvitation = statusPending;

    const isLowerRol = (role) => role === SELLER || role === ANALISTA_ONUS;
    const canEdit =
      (canMakeEdit || canMakeEditRL) &&
      (statusPending || statusApproved) &&
      ((!user.is_legal_agent && !isLowerRol(currentRoles[0])) || isMyUser);

    const canBlockAndUnblock =
      !isMyUser &&
      !user.is_legal_agent &&
      canMakeUnsubscribe &&
      canBeBlockedByCurrentRole(user.roles[0]);
    const canBlock = canBlockAndUnblock && !statusBlocked && !statusPending;
    const canUnblock = canBlockAndUnblock && statusBlocked;

    // FIXME: está raro esto. Se podría chequear directamente contra una key de RBAC en lugar
    // de preguntar qué rol tiene asignado el user.
    const isSuperiorRol = (role) =>
      role === ADMIN || role === SUPER_ADMIN_ONUS || role === ADMIN_ONUS;
    const canEditRol =
      !isMyUser &&
      canMakeEdit &&
      !user.is_legal_agent &&
      !statusBlocked &&
      isSuperiorRol(currentRoles[0]);

    const canDelete = !isMyUser && !user.is_legal_agent && canMakeDelete && canMakeUnsubscribe;

    return {
      canResendInvitation,
      canEdit,
      canBlock,
      canUnblock,
      canEditRol,
      canDelete,
    };
  };

  return {
    users,
    resendInvitation,
    selectUser,
    inviteUser,
    update,
    updatePhone,
    selected,
    unsubscribeUser,
    deleteUser,
    filter,
    changeFilter,
    resetFilter,
    getPermissionActionsByUser,
  };
};

export default useUser;
